import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BookOpen, Download, Layers, Wand2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const CATEGORY_LABEL = {
  core_reading: 'Core Reading',
  phonics_foundation: 'Phonics Foundation',
  phonemic_awareness: 'Phonemic Awareness',
};
const CATEGORY_COLOR = {
  core_reading: 'bg-blue-100 text-blue-700 border-blue-300',
  phonics_foundation: 'bg-purple-100 text-purple-700 border-purple-300',
  phonemic_awareness: 'bg-rose-100 text-rose-700 border-rose-300',
};

export default function CrosswalkTool() {
  const [programs, setPrograms] = useState([]);
  const [programId, setProgramId] = useState('');
  const [grade, setGrade] = useState('');
  const [widaLevel, setWidaLevel] = useState(3);
  const [modality, setModality] = useState('reading');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/crosswalk/programs`, { headers: authHeader() })
      .then(r => setPrograms(r.data.programs || []))
      .catch(e => setError(e?.response?.data?.detail || e.message));
  }, []);

  const selectedProgram = programs.find(p => p.id === programId);
  const availableGrades = selectedProgram?.grades || [];

  useEffect(() => {
    if (selectedProgram && !availableGrades.includes(grade)) {
      setGrade(availableGrades[0] || '');
    }
  }, [programId]);

  const handleLookup = async () => {
    if (!programId || !grade) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/crosswalk/lookup`, {
        headers: authHeader(),
        params: { program_id: programId, grade, wida_level: widaLevel, modality },
      });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePdf = async () => {
    if (!programId || !grade) return;
    const token = localStorage.getItem('session_token');
    const url = `${API}/crosswalk/chart-pdf?program_id=${programId}&grade=${grade}`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `MOSAIC_Crosswalk_${programId}_G${grade}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  };

  return (
    <div className="space-y-6" data-testid="crosswalk-tool">
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Layers className="text-blue-600" size={22} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">WIDA ↔ Curriculum Crosswalk</h2>
            <p className="text-sm text-gray-600 mt-1">
              Pair a student's proficiency level with your core reading / phonics program.
              Get unit-level alignment, sentence frames, and scaffolds for daily instruction.
            </p>
          </div>
        </div>

        {/* Program selector grid */}
        <div className="mt-6">
          <label className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2 block">1. Choose Your Program</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {programs.map(p => (
              <button
                key={p.id}
                onClick={() => setProgramId(p.id)}
                data-testid={`program-${p.id}`}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  programId === p.id
                    ? 'border-blue-600 bg-blue-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <BookOpen size={14} className="text-gray-500" />
                  <span className="font-semibold text-gray-900 text-sm">{p.name}</span>
                </div>
                <div className="text-xs text-gray-500 mb-2">{p.publisher}</div>
                <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${CATEGORY_COLOR[p.category] || ''}`}>
                  {CATEGORY_LABEL[p.category] || p.category}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        {programId && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1 block">2. Grade</label>
              <select value={grade} onChange={e => setGrade(e.target.value)} data-testid="grade-select" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                {availableGrades.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1 block">3. Proficiency Level</label>
              <select value={widaLevel} onChange={e => setWidaLevel(Number(e.target.value))} data-testid="wida-level-select" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                <option value={1}>1 · Entering</option>
                <option value={2}>2 · Emerging</option>
                <option value={3}>3 · Developing</option>
                <option value={4}>4 · Expanding</option>
                <option value={5}>5 · Bridging</option>
                <option value={6}>6 · Reaching</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1 block">4. Modality</label>
              <select value={modality} onChange={e => setModality(e.target.value)} data-testid="modality-select" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                <option value="reading">Reading</option>
                <option value="listening">Listening</option>
                <option value="speaking">Speaking</option>
                <option value="writing">Writing</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={handleLookup} disabled={loading || !grade} className="flex-1" data-testid="crosswalk-lookup-btn">
                <Wand2 size={16} className="mr-1" /> {loading ? 'Looking up…' : 'Get Scaffolds'}
              </Button>
              <Button onClick={handlePdf} disabled={!programId || !grade} variant="outline" data-testid="crosswalk-pdf-btn">
                <Download size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start gap-2" data-testid="crosswalk-error">
          <AlertCircle size={18} className="mt-0.5"/> <span>{error}</span>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm" data-testid="crosswalk-result">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold uppercase tracking-wider">
              {result.program.name} · Grade {result.grade}
            </span>
            <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">
              WIDA {result.wida_level} · {result.wida_level_name}
            </span>
            <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold uppercase">
              {result.modality}
            </span>
          </div>

          <div className="mb-5 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-xs uppercase tracking-wide text-blue-700 font-semibold mb-1">Planning Tip</div>
            <div className="text-sm text-gray-800">{result.planning_tip}</div>
          </div>

          <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold mb-1">{result.modality.toUpperCase()} Guidance</div>
            <div className="text-sm text-gray-800">{result.modality_guidance}</div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2"><Layers size={14}/> Recommended {result.program.name} Units</h3>
              <ul className="space-y-2" data-testid="recommended-units">
                {result.recommended_units.map(u => (
                  <li key={u.id} className="p-3 border border-gray-200 rounded-md bg-gray-50">
                    <div className="font-semibold text-gray-900 text-sm">{u.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {u.theme || u.focus || u.essential_question || u.skills}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-bold text-gray-800 mb-3">Scaffolds by Proficiency Level</h3>
              <div className="space-y-3">
                <div className="p-3 border border-gray-200 rounded-md">
                  <div className="text-xs font-bold uppercase text-emerald-700 mb-1">Sensory</div>
                  <div className="text-sm text-gray-700">{result.scaffolds.sensory}</div>
                </div>
                <div className="p-3 border border-gray-200 rounded-md">
                  <div className="text-xs font-bold uppercase text-blue-700 mb-1">Graphic</div>
                  <div className="text-sm text-gray-700">{result.scaffolds.graphic}</div>
                </div>
                <div className="p-3 border border-gray-200 rounded-md">
                  <div className="text-xs font-bold uppercase text-purple-700 mb-1">Interactive</div>
                  <div className="text-sm text-gray-700">{result.scaffolds.interactive}</div>
                </div>
              </div>

              <h3 className="text-sm font-bold text-gray-800 mt-4 mb-2">Sentence Frames</h3>
              <ul className="space-y-1.5">
                {result.sentence_frames.map((f, i) => (
                  <li key={i} className="text-sm p-2 bg-purple-50 border-l-4 border-purple-400 rounded-r font-mono text-gray-800" data-testid={`frame-${i}`}>
                    {f}
                  </li>
                ))}
              </ul>

              <h3 className="text-sm font-bold text-gray-800 mt-4 mb-1">Vocabulary Focus</h3>
              <div className="text-sm text-gray-700 p-2 bg-yellow-50 border border-yellow-200 rounded">
                {result.vocabulary_focus}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
